/**
 * #131 T2 — the free-tier limit policy, owned by REDUX.
 *
 * Why a slice and not component state / a hook in the dialog:
 *   - The scheduling composer is mounted LAZILY (DeferUntilOpen in the extension; the
 *     calendar mounts it only while open) and, on WhatsApp Web, is torn down and remounted
 *     constantly by DOM churn. Any lifecycle held in that component — a `useState` retry
 *     counter, a `setTimeout` — is reset on remount and cancelled on unmount. The policy
 *     would be re-fetched, retried, and re-settled over and over.
 *   - Worse, fetching AT open means the first render has no policy. Falling back to control
 *     while in flight showed every treatment-arm user a paywall flash, fired a FALSE
 *     `scheduling_wall_hit` stamped `limit_arm: 'control'`, and triggered a real
 *     "limit reached" upsell email — for a limit they were not subject to.
 *
 * So the policy is fetched ONCE AT BOOT (post-auth, alongside the other startup loads),
 * retried here with backoff, and parked in the store. The composer only ever SELECTS it,
 * and renders nothing until `status` has settled.
 *
 * FAIL CLOSED everywhere: until the policy is `ready`, and forever if it errors, the gate
 * sees FREE_BASELINE_POLICY — exactly the free plan the backend hands every free user
 * (3 concurrent since #131 T2 concluded). A fetch failure must never hand out a free
 * upgrade, and must never invent a stricter limit than the one the paywall promises.
 */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CommonRootState } from '@common/types/common-root-state-type';
import { FREE_BASELINE_POLICY, FreeLimitPolicy, isUsablePolicy } from './free-limit-gate';
import { backendApi } from './backend-api/backend-api';

export type FreeLimitStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface FreeLimitState {
  /** null until a well-formed policy arrives. Consumers read FREE_BASELINE_POLICY instead. */
  policy: FreeLimitPolicy | null;
  status: FreeLimitStatus;
  /**
   * The wall-episode key we've already recorded a `scheduling_wall_hit` for, or null.
   *
   * This dedup lives in REDUX, not a component ref, ON PURPOSE: the scheduling composer is
   * torn down and remounted constantly on WhatsApp Web, so a `useRef` "already fired" flag
   * resets on every remount — and a remount while the wall is still up would re-fire the
   * event, double-counting wall-hits and contaminating the #131 experiment. The slice
   * survives remounts, so the dedup holds across them.
   */
  wallHitKey: string | null;
}

const initialState: FreeLimitState = { policy: null, status: 'idle', wallHitKey: null };

const MAX_RETRIES = 2;
const BACKOFF_MS = 800;

const delay = (ms: number) => new Promise<void>((resolve) => { setTimeout(resolve, ms); });

/**
 * Fetch the policy, retrying transient failures with linear backoff.
 *
 * The retry lives HERE, not in a component: a transient failure would otherwise park a
 * TREATED user in control for the whole tab. That is not a leak (control is today's
 * behaviour), but the offline analysis recomputes the arm from userId and would count them
 * as treated while they behave as control — diluting the effect — and their limit would
 * appear to flicker between sessions.
 */
export const fetchFreeLimit = createAsyncThunk<
  FreeLimitPolicy,
  void,
  { state: CommonRootState }
>('freeLimit/fetch', async (_arg, thunkApi) => {
  let lastError: unknown;
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      // The RTK Query endpoint is the transport (one HTTP definition, correct auth headers,
      // works in BOTH the extension and the web app). This slice owns the lifecycle.
      return await thunkApi
        .dispatch(backendApi.endpoints.getFreeLimit.initiate(undefined, { forceRefetch: true }))
        .unwrap();
    } catch (error) {
      lastError = error;
      if (attempt < MAX_RETRIES) await delay(BACKOFF_MS * (attempt + 1));
    }
  }
  throw lastError;
});

const freeLimitSlice = createSlice({
  name: 'freeLimit',
  initialState,
  reducers: {
    /** Mark that we've recorded a wall-hit for this wall episode (idempotent across remounts). */
    wallHitRecorded(state, action: PayloadAction<string>) {
      state.wallHitKey = action.payload;
    },
    /** The wall closed — re-arm so the next distinct wall episode records again. */
    wallCleared(state) {
      state.wallHitKey = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFreeLimit.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchFreeLimit.fulfilled, (state, action) => {
        // Guard the payload here too: a malformed body must not become the live policy.
        state.policy = isUsablePolicy(action.payload) ? action.payload : null;
        state.status = 'ready';
      })
      .addCase(fetchFreeLimit.rejected, (state) => {
        // Settled, but with no policy -> selectors serve FREE_BASELINE_POLICY (fail closed).
        state.policy = null;
        state.status = 'error';
      });
  },
});

export const FreeLimitReducer = freeLimitSlice.reducer;
export const { wallHitRecorded, wallCleared } = freeLimitSlice.actions;

/** The wall episode we've already recorded a wall-hit for (null = none / re-armed). */
export const selectWallHitKey = (state: CommonRootState): string | null =>
  state.FreeLimitReducer?.wallHitKey ?? null;

/** The live policy, or the free plan's own baseline if it isn't (yet) known. */
export const selectFreeLimitPolicy = (state: CommonRootState): FreeLimitPolicy => {
  const policy = state.FreeLimitReducer?.policy;
  return isUsablePolicy(policy) ? policy : FREE_BASELINE_POLICY;
};

/**
 * Has the policy resolved one way or the other?
 *
 * The gate MUST NOT be evaluated — and no wall may be shown, and no `scheduling_wall_hit`
 * fired — until this is true, or we wall people against a policy we haven't read yet.
 * An error counts as settled: we've given up and control is the honest answer.
 */
export const selectFreeLimitSettled = (state: CommonRootState): boolean => {
  const status = state.FreeLimitReducer?.status;
  return status === 'ready' || status === 'error';
};

/** Raw fetch status — used to trigger a load if nothing else has (see useEnsureFreeLimit). */
export const selectFreeLimitStatus = (state: CommonRootState): FreeLimitStatus =>
  state.FreeLimitReducer?.status ?? 'idle';
